import React, { useEffect, useState } from "react";
import supabase from "../supabase";
import { listProjectsForUser } from "../lib/projects";
import SubmitRequestForm from "./SubmitRequestForm";
import MyRequestsTab from "./MyRequestsTab";

export default function RequestCenterPage({ profile, organization }) {
  const [activeTab, setActiveTab] = useState("submit");
  const [orgProjects, setOrgProjects] = useState([]);

  useEffect(() => {
    if (!profile?.company_id) return;

    const fetchProjects = async () => {
      try {
        const list = await listProjectsForUser({
          user_id: profile?.id,
          role: profile?.role,
          company_id: profile?.company_id,
        });
        setOrgProjects(list);
      } catch (e) {
        console.error("Failed to load projects for request form", e);
      }
    };

    fetchProjects();
  }, [profile?.id, profile?.role, profile?.company_id]);

  const handleRequestSubmit = async (payload) => {
    try {
      // 1. Separate the raw files from the data payload
      const filesToUpload = payload.rawAssets || [];
      delete payload.rawAssets; // Remove it so we don't try to save raw objects to JSONB

      let uploadedFileUrls = [];

      // 2. Upload files to Supabase Storage if any exist
      if (filesToUpload.length > 0) {
        for (const file of filesToUpload) {
          // Create a unique file name to prevent overwriting
          const fileExt = file.name.split(".").pop();
          const fileName = `${Date.now()}-${Math.random()
            .toString(36)
            .substring(7)}.${fileExt}`;
          const filePath = `${profile?.company_id}/${fileName}`;

          const { error: uploadError } = await supabase.storage
            .from("request-assets")
            .upload(filePath, file);

          if (uploadError) throw uploadError;

          // Get the public URL
          const {
            data: { publicUrl },
          } = supabase.storage.from("request-assets").getPublicUrl(filePath);

          uploadedFileUrls.push({
            name: file.name,
            url: publicUrl,
            type: file.type,
          });
        }
      }

      // 3. Attach the successful URLs back to the payload
      payload.uploaded_assets = uploadedFileUrls;

      // 4. Save to the Requests table
      const { error } = await supabase.from("Requests").insert({
        company_id: profile?.company_id,
        created_by: profile?.id,
        project_id: payload.project_id || null,
        title: payload.title || "Untitled Request",
        status: "Pending",
        brief_data: payload, // Now contains a clean array of file URLs!
      });

      if (error) throw error;
      alert("Request and assets submitted successfully!");
    } catch (err) {
      console.error("Error submitting request:", err);
      alert("Failed to submit request. Check console for details.");
    }
  };

  return (
    <div>
      <h1 style={styles.title}>Requests</h1>

      <div style={styles.tabBar}>
        <button
          type="button"
          onClick={() => setActiveTab("submit")}
          style={activeTab === "submit" ? styles.tabActive : styles.tab}
        >
          Submit Request
        </button>
        <button
          type="button"
          onClick={() => setActiveTab("my-requests")}
          style={activeTab === "my-requests" ? styles.tabActive : styles.tab}
        >
          My Requests
        </button>
      </div>

      {activeTab === "submit" ? (
        <SubmitRequestForm
          onSubmit={handleRequestSubmit}
          onSubmitted={() => setActiveTab("my-requests")}
          currentUser={profile}
          projects={orgProjects}
        />
      ) : (
        <MyRequestsTab profile={profile} organization={organization} />
      )}
    </div>
  );
}

const styles = {
  title: { fontSize: 24, fontWeight: 800, margin: "0 0 20px", color: "#111827" },
  tabBar: {
    display: "flex",
    gap: 4,
    borderBottom: "1px solid #e5e7eb",
    marginBottom: 24,
  },
  tab: {
    padding: "10px 18px",
    fontSize: 14,
    fontWeight: 700,
    color: "#6b7280",
    background: "transparent",
    border: "none",
    borderBottom: "2px solid transparent",
    cursor: "pointer",
  },
  tabActive: {
    padding: "10px 18px",
    fontSize: 14,
    fontWeight: 700,
    color: "#00876e",
    background: "transparent",
    border: "none",
    borderBottom: "2px solid #00876e",
    cursor: "pointer",
  },
};
